import { i18n } from './i18n';

/**
 * The parts of a Zod 3 issue these helpers read.
 *
 * Structural rather than imported, so this package needs no Zod of its own: any function typed
 * against it is assignable to Zod's `ZodErrorMap`, whose issue type has all of these and more.
 */
export type ZodIssueLike = {
  code: string;
  type?: string;
  minimum?: unknown;
  maximum?: unknown;
  inclusive?: boolean;
  exact?: boolean;
  expected?: unknown;
  received?: unknown;
  validation?: unknown;
  multipleOf?: unknown;
};

type ZodErrorMapLike = (issue: ZodIssueLike, ctx: { defaultError: string }) => { message: string };

/** One message, resolved when the issue is raised — never when the schema is built. */
export type IssueMessage = (issue: ZodIssueLike) => string;

/**
 * Zod's built-in messages, in the user's language.
 *
 * Install it once per app with `z.setErrorMap(zodErrorMap)`. The English copies Zod's own default
 * wording exactly, so nothing changes for an English user; what this buys is the same messages in
 * every other language. An issue it does not cover — a date bound, an enum, a literal — keeps
 * Zod's own text rather than a vaguer one of ours.
 *
 * A schema's own message still wins over this, as in Zod: this only fills in where a schema said
 * nothing.
 */
export const zodErrorMap: ZodErrorMapLike = (issue, ctx) => ({
  message: defaultMessageFor(issue) ?? ctx.defaultError,
});

const defaultMessageFor = (issue: ZodIssueLike): string | undefined => {
  switch (issue.code) {
    case 'invalid_type':
      return issue.received === 'undefined'
        ? i18n.t('validation:required')
        : i18n.t('validation:invalidType', { expected: issue.expected, received: issue.received });
    case 'invalid_union':
    case 'custom':
      return i18n.t('validation:invalidInput');
    case 'invalid_date':
      return i18n.t('validation:invalidDate');
    case 'invalid_string':
      return issue.validation === 'email' || issue.validation === 'url' || issue.validation === 'uuid'
        ? i18n.t(`validation:invalidString.${issue.validation}`)
        : undefined;
    case 'too_small':
      return sizeMessage(SIZE_KEYS.tooSmall, issue.minimum, issue);
    case 'too_big':
      return sizeMessage(SIZE_KEYS.tooBig, issue.maximum, issue);
    case 'not_multiple_of':
      return i18n.t('validation:notMultipleOf', { value: String(issue.multipleOf) });
    case 'not_finite':
      return i18n.t('validation:notFinite');
    default:
      return undefined;
  }
};

/**
 * Every too-small and too-big key, spelled out so each is a real, type-checked key: which one applies
 * depends on what was measured and on whether the bound itself is allowed.
 */
const SIZE_KEYS = {
  tooSmall: {
    string: {
      exact: 'validation:tooSmall.string.exact',
      inclusive: 'validation:tooSmall.string.atLeast',
      exclusive: 'validation:tooSmall.string.over',
    },
    array: {
      exact: 'validation:tooSmall.array.exact',
      inclusive: 'validation:tooSmall.array.atLeast',
      exclusive: 'validation:tooSmall.array.over',
    },
    number: {
      exact: 'validation:tooSmall.number.exact',
      inclusive: 'validation:tooSmall.number.atLeast',
      exclusive: 'validation:tooSmall.number.over',
    },
  },
  tooBig: {
    string: {
      exact: 'validation:tooBig.string.exact',
      inclusive: 'validation:tooBig.string.atMost',
      exclusive: 'validation:tooBig.string.under',
    },
    array: {
      exact: 'validation:tooBig.array.exact',
      inclusive: 'validation:tooBig.array.atMost',
      exclusive: 'validation:tooBig.array.under',
    },
    number: {
      exact: 'validation:tooBig.number.exact',
      inclusive: 'validation:tooBig.number.atMost',
      exclusive: 'validation:tooBig.number.under',
    },
  },
} as const;

type SizeKeys = (typeof SIZE_KEYS)['tooSmall' | 'tooBig'];

/** A too-small or too-big message. Counts go through `count` so a language with plurals can use them. */
const sizeMessage = (keys: SizeKeys, bound: unknown, issue: ZodIssueLike): string | undefined => {
  const kind = issue.exact ? 'exact' : issue.inclusive ? 'inclusive' : 'exclusive';

  if (issue.type === 'string' || issue.type === 'array') {
    return i18n.t(keys[issue.type][kind], { count: Number(bound) });
  }

  if (issue.type === 'number' || issue.type === 'bigint') {
    return i18n.t(keys.number[kind], { value: String(bound) });
  }

  return undefined;
};

type FieldMessages = {
  /** An empty value: `min(1)`, or the field missing altogether. */
  required?: IssueMessage;
  /** Shorter or smaller than allowed, past the first character. */
  min?: IssueMessage;
  /** Longer or larger than allowed. */
  max?: IssueMessage;
  /** A failed `.regex()`: what the pattern requires, since Zod's own "Invalid" says nothing. */
  pattern?: IssueMessage;
  /** A value of the wrong type, `null` included — Zod's `invalid_type_error`. */
  invalidType?: IssueMessage;
};

/**
 * One field's messages, picked by issue, as a schema-level `errorMap`:
 *
 * ```ts
 * name: z.string({ errorMap: fieldMessages({ required: () => i18n.t('boats:validation.enterName') }) })
 *   .trim().min(1).max(50)
 * ```
 *
 * Zod 3 takes only a fixed string in `.min(1, '…')`, which would fix the language when the module
 * loads. An error map runs when the issue is raised, so the message is in the language the user is
 * in at that moment. The bound is read off the issue, so a limit is written once — in the check.
 *
 * A `min` of 1 counts as `required`: a one-character minimum is how an empty string is refused.
 * Anything not covered here falls through to the app's `zodErrorMap`.
 */
export const fieldMessages =
  ({ required, min, max, pattern, invalidType }: FieldMessages): ZodErrorMapLike =>
  (issue, ctx) => {
    if (issue.code === 'invalid_type' && issue.received === 'undefined' && required) {
      return { message: required(issue) };
    }

    if (issue.code === 'invalid_type' && issue.received !== 'undefined' && invalidType) {
      return { message: invalidType(issue) };
    }

    if (issue.code === 'too_small') {
      const resolve = Number(issue.minimum) <= 1 && required ? required : min;

      if (resolve) {
        return { message: resolve(issue) };
      }
    }

    if (issue.code === 'too_big' && max) {
      return { message: max(issue) };
    }

    if (issue.code === 'invalid_string' && issue.validation === 'regex' && pattern) {
      return { message: pattern(issue) };
    }

    return { message: zodErrorMap(issue, ctx).message };
  };

/**
 * The message for a `.refine()`, resolved when the check fails:
 *
 * ```ts
 * mmsi: z.string().refine(isMmsi, refineMessage(() => i18n.t('boats:validation.mmsi')))
 * ```
 *
 * `refine` accepts a function in place of a message for exactly this.
 */
export const refineMessage = (message: () => string) => () => ({ message: message() });

/** `{{field}} must be at least {{min}} characters.`, with the bound off the issue. */
export const minLengthMessage =
  (field: () => string): IssueMessage =>
  (issue) =>
    i18n.t('validation:minLength', { field: field(), min: String(issue.minimum) });

/** `{{field}} must be at most {{max}} characters.`, with the bound off the issue. */
export const maxLengthMessage =
  (field: () => string): IssueMessage =>
  (issue) =>
    i18n.t('validation:maxLength', { field: field(), max: String(issue.maximum) });
